/**
 * Activity Composable
 * 管理活動列表和詳情
 * 原始來源: cuora-module/src/machine/activity/activity.js
 *
 * 架構：
 * - 狀態管理：使用 Pinia store (stores/activity/activity.js)
 * - 業務邏輯：保留在 composable 中
 * - 單例模式：使用 nuxtApp._activityContext 避免 watch/storeToRefs 重複創建
 */

import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, watch, getCurrentInstance } from 'vue'

import { useFetch, useNuxtApp, useRuntimeConfig } from '#app'
import { activityApi } from '@/api'
import { useAuthToken } from '@/composables/auth/useAuthToken'
import { useUserInfo } from '@/composables/user/useUserInfo'
import { useActivityStore } from '@/stores/activity/activity'
import { useNavbarStore } from '@/stores/ui/navbar'
import { storage } from '@/utils/storage'

export const useActivity = (name) => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保 watch、storeToRefs、useFetch 只創建一次
  if (!nuxtApp._activityContext) {
    const { $request } = nuxtApp
    const { apiUrl, clientId, appSecret, intranet } = useRuntimeConfig().public
    const { getUserInfo } = useUserInfo()
    const store = useActivityStore()
    const navbarStore = useNavbarStore()
    const { isLoggedIn } = useAuthToken()

    // storeToRefs 只調用一次
    const storeRefs = storeToRefs(store)

    const uidSite = computed(() => getUserInfo.value?.uid_site || '')

    // 根據環境決定 API base URL
    const baseUrl = import.meta.server && intranet === '1' ? 'http://frontend-api-v2.default.svc.cluster.local' : apiUrl

    // 從 activityApi 取得 API 配置
    const allActivitiesApiConfig = activityApi.getAllActivityList({ appSecret })
    const allActivitiesApiPath = allActivitiesApiConfig.url.replace(':clientId', clientId)

    // 使用 useFetch 自動執行（SSR 友善）
    // 使用 onResponse 確保 SSR 階段資料同步處理，避免 Hydration 時序問題
    useFetch(`${baseUrl}/${allActivitiesApiPath}`, {
      key: 'all-activities',
      dedupe: 'defer',
      query: allActivitiesApiConfig.params,
      getCachedData(key, nuxtApp) {
        return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
      },
      onResponse({ response }) {
        const activities = response._data?.data || []
        store.setPublicActivityList(activities)
      },
      onResponseError({ error: err }) {
        console.error('[useActivity] fetchAllActivities API error:', err)
        store.setPublicActivityList([])
      },
    })

    // 安全解析 localStorage JSON 數據
    const safeParseLocalStorage = (key) => {
      // SSR 保護：server 端沒有 localStorage
      if (typeof window === 'undefined') return {}
      try {
        const item = localStorage.getItem(key)
        if (!item) return {}
        return JSON.parse(item) || {}
      } catch (error) {
        console.warn(`[useActivity] Failed to parse localStorage key "${key}":`, error)
        return {}
      }
    }

    const setActivityList = (activities) => {
      // 從 localStorage 讀取未讀狀態
      const unreadList = safeParseLocalStorage(`${uidSite.value}_unread`)
      // 不需要標記為未讀的活動類型
      const noReadActivity = [2, 4, 13, 20, 90, 91, 92]

      const listWithUnread = activities.map((activity) => {
        if (noReadActivity.includes(activity.activityType)) {
          activity.unread = false
        } else if (unreadList[activity.activityNo] === undefined) {
          activity.unread = true
        } else {
          activity.unread = unreadList[activity.activityNo]
        }
        return activity
      })

      store.setLoggedInActivityList(listWithUnread)

      const map = activities.reduce((acc, activity) => {
        acc[activity.activityNo] = activity
        return acc
      }, {})

      store.setActivityMap(map)
    }

    const setTournamentList = (activities) => {
      const tournaments = activities.filter((activity) => activity.activityType === 9)
      store.setTournamentList(tournaments)
    }

    const fetchActivitiesDetail = async () => {
      if (!isLoggedIn.value) return

      const needDetailActivities = storeRefs.loggedInActivityList.value.filter((activity) =>
        storeRefs.specifyActivities.value.includes(activity.activityType)
      )

      if (needDetailActivities.length === 0) {
        store.setActivitiesDetailList([])
        return
      }

      try {
        const activityNos = needDetailActivities.map((a) => a.activityNo)
        const response = await Promise.all(
          activityNos.map((activityNo) =>
            $request.send(
              activityApi.getActivityDetail({
                activityNo,
                appSecret,
              })
            )
          )
        )

        const oldDataMap = new Map(storeRefs.activitiesDetailList.value.map((d) => [d?.activityNo, d]))

        // 處理新資料，空物件則用舊資料
        // 預防頻繁呼叫回傳空物件的情況 response: { data: {} }
        const validDetails = response
          .map((item, index) => {
            const newData = item.data
            const isEmptyData = !newData || Object.keys(newData).length === 0
            return isEmptyData ? oldDataMap.get(activityNos[index]) : newData
          })
          .filter(Boolean)

        store.setLoading(false)
        store.setActivitiesDetailList(validDetails)
      } catch (error) {
        console.error('[useActivity] fetchActivitiesDetail error:', error)
        store.setActivitiesDetailList([])
      }
    }

    const fetchList = () => {
      if (!isLoggedIn.value) {
        return Promise.resolve()
      }

      if (store.fetchPromise) {
        return store.fetchPromise
      }

      store.setLoading(true)

      store.fetchPromise = (async () => {
        try {
          const [activityRes, promotionRes] = await Promise.all([
            $request.send(activityApi.getActivityList({ appSecret })),
            $request.send(activityApi.getPromotionList({ appSecret })),
          ])

          const activities = activityRes.data || []
          const promotions = promotionRes || []

          setActivityList(activities)
          store.setPromotionList(promotions)
          setTournamentList(activities)
          await fetchActivitiesDetail()

          return { activities, promotions }
        } catch (err) {
          console.error('[useActivity] fetchList error:', err)
          throw err
        } finally {
          store.setLoading(false)
          store.fetchPromise = null
        }
      })()

      return store.fetchPromise
    }

    const setUnread = (activityNo, unread = false) => {
      const activity = storeRefs.loggedInActivityList.value.find((a) => a.activityNo === activityNo)
      if (activity) {
        activity.unread = unread
      }

      // SSR 保護：server 端沒有 localStorage
      if (typeof window === 'undefined') return

      const unreadList = safeParseLocalStorage(`${uidSite.value}_unread`)
      unreadList[activityNo] = unread
      localStorage.setItem(`${uidSite.value}_unread`, JSON.stringify(unreadList))
    }

    const refresh = async () => {
      await fetchList()
    }

    const resetActivities = () => {
      store.setIsPlaying(true)
    }

    const leaveGame = () => {
      store.setIsPlaying(false)
    }

    const startAutoRefresh = () => {
      if (!import.meta.client) return

      fetchList()

      if (storeRefs.refreshTimer.value) return

      const timer = setInterval(async () => {
        await fetchList()
      }, storeRefs.tick.value * 10)

      store.setRefreshTimer(timer)
    }

    const stopAutoRefresh = (force = false) => {
      // SSR 環境跳過
      if (!import.meta.client) {
        return
      }

      // force 參數允許在 logout 時強制停止，忽略 instance 計數
      if (!force && storeRefs.activeInstances.value.size > 0) {
        return
      }

      // 清除 timer
      if (storeRefs.refreshTimer.value) {
        clearInterval(storeRefs.refreshTimer.value)
        store.setRefreshTimer(null)
        const instanceCount = storeRefs.activeInstances.value.size
        if (instanceCount > 0) return

        if (storeRefs.refreshTimer.value) {
          clearInterval(storeRefs.refreshTimer.value)
          store.setRefreshTimer(null)
        }
      }
    }

    const friendActivity = computed(() => store.getFriendActivity)
    const getFriendsActivityDetail = computed(() => storeRefs.friendsActivityDetail.value)

    const navbarSettings = computed(() => {
      const navbarData = navbarStore.navbar || []
      const activityListData = storeRefs.activityList.value || []

      if (activityListData.length > 0) {
        return navbarData.filter((item) => {
          if (item.type === 'ACTIVITY') {
            return activityListData.find((activity) => activity.activityNo === item.activityNo)
          } else if (item.type === 'PROMOTIONS') {
            return activityListData.find((promotion) => promotion.activityNo === item.promotionsNo)
          } else {
            return true
          }
        })
      } else {
        return navbarData
      }
    })

    const fetchFriendsActivityDetail = async (activityNo) => {
      if (!activityNo) {
        console.warn('[useActivity] fetchFriendsActivityDetail: activityNo is required')
        return null
      }

      try {
        const response = await $request.send(
          activityApi.getActivityDetail({
            activityNo,
            appSecret,
          })
        )
        store.setFriendsActivityDetail(response.data)
        return response.data
      } catch (error) {
        console.error('[useActivity] fetchFriendsActivityDetail error:', error)
        store.setFriendsActivityDetail(null)
        return null
      }
    }

    // SSR 清理方法（供 ssr-cleanup.server.js 調用）
    const cleanup = () => {
      // 清理 timer
      if (storeRefs.refreshTimer.value) {
        clearInterval(storeRefs.refreshTimer.value)
        store.setRefreshTimer(null)
      }
      // 清空 activeInstances，防止 SSR 環境中累積
      store.clearActiveInstances()
    }

    nuxtApp._activityContext = {
      // Store refs
      tick: storeRefs.tick,
      specifyActivities: storeRefs.specifyActivities,
      activitiesDetailList: storeRefs.activitiesDetailList,
      activityList: storeRefs.activityList,
      publicActivityList: storeRefs.publicActivityList,
      promotionList: storeRefs.promotionList,
      activityMap: storeRefs.activityMap,
      tournamentList: storeRefs.tournamentList,
      refs: storeRefs.refs,
      siteConfig: storeRefs.siteConfig,
      isPlaying: storeRefs.isPlaying,
      loading: storeRefs.loading,
      hasFriendActivity: storeRefs.hasFriendActivity,

      // Computed
      uidSite,
      friendActivity,
      getFriendsActivityDetail,
      navbarSettings,

      // Methods
      fetchList,
      setUnread,
      refresh,
      resetActivities,
      leaveGame,
      startAutoRefresh,
      stopAutoRefresh,
      fetchFriendsActivityDetail,
      cleanup,

      // Internal (for lifecycle hooks and client-side watch)
      _store: store,
      _isLoggedIn: isLoggedIn,
      _clientWatchCreated: false,
    }
  }

  const ctx = nuxtApp._activityContext

  // 客戶端專屬：同步 isLoggedIn 狀態
  // 每次 useActivity() 呼叫時直接同步，確保狀態一致
  if (import.meta.client) {
    // 直接同步 isLoggedIn 狀態到 store
    ctx._store.setIsLoggedIn(ctx._isLoggedIn.value)

    // 創建 watch 監聽後續變化（如果還沒創建）
    if (!ctx._clientWatchCreated) {
      watch(ctx._isLoggedIn, (value) => {
        ctx._store.setIsLoggedIn(value)
        if (value) {
          ctx.fetchList()
        }
      })
      ctx._clientWatchCreated = true
    }
  }

  // 生命週期 hooks（每次調用都需要執行）
  if (getCurrentInstance()) {
    onMounted(() => {
      ctx._store.registerInstance(name)
      ctx.startAutoRefresh()

      if (Array.isArray(ctx.publicActivityList.value)) {
        storage.set('public-activity-list', ctx.publicActivityList.value)
      }
    })

    onUnmounted(() => {
      ctx._store.unregisterInstance(name)
      ctx.stopAutoRefresh()
    })
  }

  return {
    // State
    tick: ctx.tick,
    specifyActivities: ctx.specifyActivities,
    activitiesDetailList: ctx.activitiesDetailList,
    activityList: ctx.activityList,
    publicActivityList: ctx.publicActivityList,
    promotionList: ctx.promotionList,
    activityMap: ctx.activityMap,
    tournamentList: ctx.tournamentList,
    refs: ctx.refs,
    siteConfig: ctx.siteConfig,
    isPlaying: ctx.isPlaying,
    loading: ctx.loading,
    uidSite: ctx.uidSite,

    // 好友活動相關
    friendActivity: ctx.friendActivity,
    getFriendsActivityDetail: ctx.getFriendsActivityDetail,
    hasFriendActivity: ctx.hasFriendActivity,

    // Navbar 設定
    navbarSettings: ctx.navbarSettings,

    // Methods
    fetchList: ctx.fetchList,
    setUnread: ctx.setUnread,
    refresh: ctx.refresh,
    resetActivities: ctx.resetActivities,
    leaveGame: ctx.leaveGame,
    stopAutoRefresh: ctx.stopAutoRefresh,
    fetchFriendsActivityDetail: ctx.fetchFriendsActivityDetail,
  }
}
